/**
 * Alert Replacement Utilities
 * Provides React-based alternatives to browser alert() calls
 */

// Global notification context reference
let globalNotificationContext = null;

/**
 * Set the global notification context
 * This should be called once from the NotificationProvider
 * @param {object} context - Notification context with show methods
 */
export const setGlobalNotificationContext = (context) => {
  globalNotificationContext = context;
};

/**
 * Show an error notification (replaces alert for errors)
 * @param {string} message - Error message
 * @param {object} options - Additional options
 */
export const showErrorNotification = (message, options = {}) => {
  if (globalNotificationContext) {
    globalNotificationContext.showError(message, {
      title: 'Error',
      autohide: false, // Errors should be manually dismissed
      ...options
    });
  } else {
    // Fallback to console in development
    console.error('[Alert Replacement]', message);
    
    // Only use alert as last resort in production if notification system fails
    if (process.env.NODE_ENV === 'production') {
      alert(message);
    }
  }
};

/**
 * Show a warning notification (replaces alert for warnings)
 * @param {string} message - Warning message
 * @param {object} options - Additional options
 */
export const showWarningNotification = (message, options = {}) => {
  if (globalNotificationContext) {
    globalNotificationContext.showWarning(message, {
      title: 'Warning',
      delay: 8000, // Longer delay for warnings
      ...options
    });
  } else {
    console.warn('[Alert Replacement]', message);
    if (process.env.NODE_ENV === 'production') {
      alert(message);
    }
  }
};

/**
 * Show an info notification (replaces alert for information)
 * @param {string} message - Info message
 * @param {object} options - Additional options
 */
export const showInfoNotification = (message, options = {}) => {
  if (globalNotificationContext) {
    globalNotificationContext.showInfo(message, {
      title: 'Information',
      delay: 5000,
      ...options
    });
  } else {
    console.info('[Alert Replacement]', message);
    if (process.env.NODE_ENV === 'production') {
      alert(message);
    }
  }
};

/**
 * Show a success notification
 * @param {string} message - Success message
 * @param {object} options - Additional options
 */
export const showSuccessNotification = (message, options = {}) => {
  if (globalNotificationContext) {
    globalNotificationContext.showSuccess(message, {
      title: 'Success',
      delay: 4000,
      ...options
    });
  } else {
    console.log('[Alert Replacement]', message);
    if (process.env.NODE_ENV === 'production') {
      alert(message);
    }
  }
};

/**
 * Show a confirmation dialog (for replace confirm() calls)
 * Note: This returns a Promise for async handling
 * @param {string} message - Confirmation message
 * @param {object} options - Additional options
 * @returns {Promise<boolean>} Promise resolving to user's choice
 */
export const showConfirmDialog = (message, options = {}) => {
  return new Promise((resolve) => {
    if (globalNotificationContext) {
      // Create a custom confirmation using notifications
      // This requires additional implementation for modal dialogs
      // For now, fallback to confirm()
      resolve(window.confirm(message));
    } else {
      resolve(window.confirm(message));
    }
  });
};

/**
 * Replace alert calls in existing code
 * This can be used to patch existing components
 * @param {string} message - Alert message
 * @param {string} type - Type of alert ('error', 'warning', 'info', 'success')
 */
export const replaceAlert = (message, type = 'info') => {
  switch (type.toLowerCase()) {
    case 'error':
      showErrorNotification(message);
      break;
    case 'warning':
      showWarningNotification(message);
      break;
    case 'success':
      showSuccessNotification(message);
      break;
    default:
      showInfoNotification(message);
  }
};

/**
 * Higher-order component to inject notification methods
 * @param {React.Component} Component - Component to wrap
 * @returns {React.Component} Wrapped component with notification props
 */
export const withNotifications = (Component) => {
  return function WrappedComponent(props) {
    const notificationMethods = {
      showError: showErrorNotification,
      showWarning: showWarningNotification,
      showInfo: showInfoNotification,
      showSuccess: showSuccessNotification,
      showConfirm: showConfirmDialog,
      replaceAlert
    };

    return <Component {...props} {...notificationMethods} />;
  };
};

/**
 * Hook for notification methods (alternative to context)
 * @returns {object} Notification methods
 */
export const useAlertReplacer = () => {
  return {
    showError: showErrorNotification,
    showWarning: showWarningNotification,
    showInfo: showInfoNotification,
    showSuccess: showSuccessNotification,
    showConfirm: showConfirmDialog,
    replaceAlert
  };
};